import axios, { AxiosError } from "axios";

declare global {
  interface Window {
    __RUNTIME_CONFIG__?: { VITE_API_BASE_URL?: string };
  }
}

// Runtime value (injected into env-config.js at container startup) wins when
// present, so one Docker image can be deployed against any API URL without a
// rebuild; the Vite build-time value and localhost are just dev-time fallbacks.
export const API_BASE_URL =
  window.__RUNTIME_CONFIG__?.VITE_API_BASE_URL || import.meta.env.VITE_API_BASE_URL || "http://localhost:4000/api";

const TOKEN_STORAGE_KEY = "dacentric_access_token";

export function getStoredToken(): string | null {
  return localStorage.getItem(TOKEN_STORAGE_KEY);
}

export function setStoredToken(token: string | null) {
  if (token) localStorage.setItem(TOKEN_STORAGE_KEY, token);
  else localStorage.removeItem(TOKEN_STORAGE_KEY);
}

export const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
});

/**
 * request 1181: inside Tools → Employee Dashboards the app runs in a frame at /dashboard-view/frame/<userId>; every request there
 * is answered as that person, read only (the server refuses anything else). Never set outside a frame.
 */
export const VIEW_AS_USER_ID: string | null = (() => {
  try {
    const m = window.location.pathname.match(/^\/dashboard-view\/frame\/([^/]+)/);
    return m && window.self !== window.top ? decodeURIComponent(m[1]) : null;
  } catch {
    return null;
  }
})();

api.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  if (VIEW_AS_USER_ID) config.headers["X-View-As"] = VIEW_AS_USER_ID;
  return config;
});

export interface ApiErrorShape {
  code: string;
  message: string;
  fieldErrors?: Record<string, string>;
}

export function extractApiError(err: unknown): ApiErrorShape {
  if (axios.isAxiosError(err)) {
    const axiosErr = err as AxiosError<{ error?: ApiErrorShape }>;
    if (axiosErr.response?.data?.error) return axiosErr.response.data.error;
    if (axiosErr.code === "ERR_NETWORK") {
      return { code: "NETWORK_ERROR", message: "Could not reach the server. Check your connection and try again." };
    }
  }
  return { code: "UNKNOWN", message: "Something went wrong. Please try again." };
}

/** Request 1278 — the server is busy or restarting, or the connection dropped: worth waiting and trying again. */
export function isTemporaryError(err: unknown): boolean {
  if (!axios.isAxiosError(err)) return false;
  const status = err.response?.status;
  return status == null ? err.code !== "ERR_CANCELED" : [408, 425, 429, 502, 503, 504].includes(status);
}

/** Request 1278 — temporary failures wait over a minute (the limit resets within one) before a page gives up; a real refusal (no access, not found) doesn't retry. */
export function retryLoad(failureCount: number, err: unknown): boolean {
  if (isTemporaryError(err)) return failureCount < 6;
  if (axios.isAxiosError(err) && err.response && err.response.status < 500) return false;
  return failureCount < 1;
}
export const retryLoadDelay = (attempt: number) => Math.min(2000 * 2 ** attempt, 20000) + Math.random() * 1000;

/** Request 1278 — the real reason a page couldn't load, instead of always blaming access. `what` is e.g. "this project". */
export function loadErrorMessage(err: unknown, what: string): string {
  const status = axios.isAxiosError(err) ? err.response?.status : undefined;
  if (status === 404) return `Could not find ${what}. It may have been deleted, or you may not have access to it.`;
  if (status === 403) return `You don't have access to ${what}.`;
  if (status === 429) return "The server is busy right now. Please try again in a minute.";
  if (isTemporaryError(err)) return "Could not reach the server. It may be restarting, or your connection dropped. Please try again in a minute.";
  return `Could not load ${what}. Please try again.`;
}

let onUnauthorized: (() => void) | null = null;
export function registerUnauthorizedHandler(handler: () => void) {
  onUnauthorized = handler;
}

// The access token is short-lived; the refresh token lives in an httpOnly
// cookie the browser sends automatically. A single in-flight promise is
// shared across every 401 that arrives at once *within this tab*, so a burst
// of concurrent requests triggers exactly one /auth/refresh call, not one
// per request.
let refreshPromise: Promise<string | null> | null = null;

async function performRefresh(): Promise<string | null> {
  // request 1278: a busy or restarting server isn't the end of the session — wait and try again before signing the person out
  for (let attempt = 0; ; attempt++) {
    try {
      const res = await axios.post(`${API_BASE_URL}/auth/refresh`, null, { withCredentials: true });
      const token = res.data?.data?.accessToken as string | undefined;
      setStoredToken(token ?? null);
      return token ?? null;
    } catch (err) {
      if (isTemporaryError(err) && attempt < 6) {
        await new Promise((resolve) => setTimeout(resolve, retryLoadDelay(attempt)));
        continue;
      }
      setStoredToken(null);
      return null;
    }
  }
}

export async function refreshAccessToken(): Promise<string | null> {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    // The refresh token rotates (single-use) — if two tabs of the same
    // origin both race to refresh at once (e.g. every tab's access token
    // expires together right when the device wakes from sleep), the second
    // request presents an already-used token and the server revokes the
    // whole session as a suspected compromise, force-logging everyone out.
    // The Web Locks API serializes this across every tab, not just this one,
    // so only one real /auth/refresh call ever happens at a time.
    if (typeof navigator !== "undefined" && "locks" in navigator) {
      return navigator.locks.request("dacentric-auth-refresh", performRefresh);
    }
    return performRefresh();
  })().finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
}

api.interceptors.response.use(
  (res) => res,
  async (err) => {
    if (!axios.isAxiosError(err) || err.response?.status !== 401) return Promise.reject(err);

    const config = err.config as (typeof err.config & { _retried?: boolean }) | undefined;
    // the viewer frame never refreshes the sign-in itself (the main window does — two refreshing at once would end the session)
    if (VIEW_AS_USER_ID) {
      const fresh = getStoredToken();
      if (config && !config._retried && fresh && config.headers?.Authorization !== `Bearer ${fresh}`) {
        config._retried = true;
        return api(config);
      }
      return Promise.reject(err);
    }
    const isRefreshCall = config?.url?.includes("/auth/refresh");
    if (!config || isRefreshCall || config._retried) {
      setStoredToken(null);
      onUnauthorized?.();
      return Promise.reject(err);
    }

    config._retried = true;
    const newToken = await refreshAccessToken();
    if (!newToken) {
      onUnauthorized?.();
      return Promise.reject(err);
    }
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${newToken}`;
    return api(config);
  }
);
